/**
 * Site-chrome copy — strings that appear on EVERY public page rather than on
 * one of them (#3123 / #3165).
 *
 * WHY THIS FILE EXISTS AT ALL, FOR ONE STRING
 * -------------------------------------------
 * Not an abstraction waiting for consumers. `scripts/check-voice.mjs` scans
 * `frontend/src/data/content/*.ts` and does NOT scan components (#2954), so a
 * string written inline in `StaticFooter.tsx` would get no mechanical check for
 * em-dashes or British spellings. This is the only way the footer's copy is
 * gated. `landing.ts` was the wrong home: that module is the front door's copy,
 * and this line is not the front door's.
 *
 * WHERE THIS RENDERS, WHICH IS WIDER THAN IT LOOKS
 * ------------------------------------------------
 * `StaticFooter` is used by `PageLayout` plus HomePage, WaitlistPage,
 * LeaderGettingStartedPage and ContactPage — so this line appears on the legal
 * pages too, Privacy and Terms included. That is deliberate (owner decision
 * 2026-08-23: the trust claims belong on every page, not only the front door),
 * but it means the bar is higher than marketing copy: a compressed claim
 * sitting a scroll away from the policy that qualifies it must not overstate
 * it.
 *
 * EVERY CLAIM HERE IS MECHANICALLY BACKED, and `StaticFooter.test.tsx` is the
 * authority on where — this comment is a reader's summary of that table, not a
 * second source. Two of the three are NOT in `privacy.ts`, which an earlier
 * version of this comment got wrong (George, PR #3173 round 2):
 *   - "No passwords"  — `terms.ts`, "uses passwordless authentication". Backed
 *                       in the terms a user is actually held to, not inferred
 *                       from the absence of a password column.
 *   - "No ads"        — `privacy.ts`, the negated "We do not ... run ads"
 *                       sentence. Deliberately NOT the "never share your data
 *                       with advertisers" line: sharing no data with
 *                       advertisers does not establish that no ads are shown,
 *                       and pinning the claim there would let the policy start
 *                       running ads with this line still on every page.
 *   - "No selling your personal information"
 *                     — `privacy.ts`, "We never sell or rent your personal
 *                       information."
 * If any of those change in their document, this line is wrong on every public
 * page at once. Treat a change to either document as a change to this file; the
 * test fails rather than letting it drift silently.
 *
 * THE OFFER LINE (#3512) is a separate string, and its backing is a business
 * doc rather than a policy, because no policy states it:
 *   - "The core is free for every club, forever."
 *                     — `docs/canonical/business/subscription-tiers.md`, "A
 *                       club owner can run their entire club — forever — on
 *                       Free" (ADR-0017). Owner decision 2026-10-05 on #3512:
 *                       "free forever" attaches to the club's CORE, never to
 *                       everything, because Club Supporter is a paid tier.
 * It stays its own line so the trust line above (and the gallery proof's copy
 * of it) keeps its exact wording.
 */

export const SITE = {
  /** Concrete trust claims. Kept to three, in the policy's own terms. */
  footerTrust: 'No passwords. No ads. No selling your personal information.',
  /** The free offer, echoed from the front door's offer line (#3512). */
  footerOffer: 'The core is free for every club, forever.',
} as const;
